import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

const STALE_TIME = 10 * 60 * 1000; // 10 minutes
const GC_TIME = 30 * 60 * 1000; // 30 minutes

interface HelpCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  sort_order: number;
}

interface HelpArticle {
  id: string;
  title: string;
  slug: string;
  category_id: string;
  content: string;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface HelpArticleDetail extends HelpArticle {
  category?: HelpCategory;
}

interface SearchResult {
  id: string;
  title: string;
  slug: string;
  category_id: string;
  excerpt: string;
}

async function fetchCategories(): Promise<HelpCategory[]> {
  const { data, error } = await supabase
    .from('help_categories')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

async function fetchArticles(categorySlug?: string): Promise<HelpArticle[]> {
  let query = supabase
    .from('help_articles')
    .select('*, help_categories(id, slug)')
    .eq('is_published', true)
    .order('sort_order', { ascending: true });

  if (categorySlug) {
    query = query.eq('help_categories.slug', categorySlug);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

async function fetchArticle(
  categorySlug: string,
  articleSlug: string
): Promise<HelpArticleDetail> {
  const { data, error } = await supabase
    .from('help_articles')
    .select('*, category:help_categories(id, name, slug)')
    .eq('slug', articleSlug)
    .eq('is_published', true)
    .eq('category.slug', categorySlug)
    .single();

  if (error) throw error;
  return data;
}

async function searchArticles(query: string): Promise<SearchResult[]> {
  // The DB function's parameter is named `query` (search_help_articles(query text)).
  const { data, error } = await supabase.rpc('search_help_articles', { query });

  if (error) throw error;
  return data || [];
}

export function useHelpCategories() {
  return useQuery({
    queryKey: queryKeys.helpCenter.categories(),
    queryFn: fetchCategories,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useHelpArticles(categorySlug?: string) {
  return useQuery({
    queryKey: queryKeys.helpCenter.articles(categorySlug),
    queryFn: () => fetchArticles(categorySlug),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useHelpArticle(categorySlug: string, articleSlug: string) {
  return useQuery({
    queryKey: queryKeys.helpCenter.article(categorySlug, articleSlug),
    queryFn: () => fetchArticle(categorySlug, articleSlug),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!categorySlug && !!articleSlug,
  });
}

export function useHelpSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.helpCenter.search(query),
    queryFn: () => searchArticles(query),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: query.length > 0,
  });
}
